'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { ESSENTIAL_COOKIES_PROPERTY_NAME } from '@cineamo/ui/src/constants/cookie-constants';

import { cookieStorage } from '@cineamo/ui/src/helpers/cookie-helper';

import { CookiePreferencesModal } from '@cineamo/ui/src/modals/CookiePreferencesModal';
import CookiesModal from '@cineamo/ui/src/modals/CookiesModal';

import LanguageDropdown from './LanguageDropdown';
import StaticFooterItem from './StaticFooterItem';

const Footer: React.FC = () => {
    const t = useTranslations();

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [cookiePreferencesModalIsOpen, setCookiePreferencesModalIsOpen] = useState<boolean>(false);
    const { domainConfiguration } = useDomainConfiguration();

    // Automatically show the cookie banner on the first visit, i.e. as long as no consent decision has been saved.
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!cookieStorage.getItem(ESSENTIAL_COOKIES_PROPERTY_NAME)) {
                setModalIsOpen(true);
            }
        }, 1500);
        return () => clearTimeout(timeout);
    }, []);

    function showPrivacySettingsModal() {
        setModalIsOpen(true);
    }

    function onShowCookiePreferences() {
        setModalIsOpen(false);
        setCookiePreferencesModalIsOpen(true);
    }

    // A landing page is legally provided by its distributor, so both documents may live on the
    // distributor's own site. Without a configured URL we fall back to Cineamo's own pages.
    const imprintUrl = domainConfiguration?.imprintUrl || `${process.env.NEXT_PUBLIC_ENV_WEBAPP_HOST_NAME}/imprint`;
    const dataProtectionUrl =
        domainConfiguration?.dataProtectionUrl || `${process.env.NEXT_PUBLIC_ENV_WEBAPP_HOST_NAME}/data-protection`;

    return (
        <footer className="w-full flex-none text-body-12 p-default-screen mt-auto pb-100 lg:pb-0">
            {modalIsOpen && (
                <CookiesModal
                    modalIsOpen={modalIsOpen}
                    setModalIsOpen={setModalIsOpen}
                    onShowCookiePreferences={onShowCookiePreferences}
                />
            )}
            {cookiePreferencesModalIsOpen && (
                <CookiePreferencesModal
                    modalIsOpen={cookiePreferencesModalIsOpen}
                    setModalIsOpen={setCookiePreferencesModalIsOpen}
                    onBackClick={() => setModalIsOpen(true)}
                />
            )}

            <div className="w-full flex-none relative flex flex-col">
                <div className="w-full -mt-2 border-b-2 border-white/20" />

                <div className="w-full flex flex-col xl:flex-row justify-between text-body-12 xl:space-x-16 px-48 my-16">
                    <div className="flex-none block lg:hidden mb-16">
                        <LanguageDropdown />
                    </div>

                    <div
                        className={classnames(
                            'flex-none flex flex-col sm:flex-row space-y-8 sm:space-y-0 sm:space-x-[24px]',
                            'items-center sm:items-start',
                            'my-8 xl:my-0',
                            'order-1 xl:order-0',
                            'mt-24 xl:mt-0'
                        )}>
                        <span className="text-center sm:text-left">&copy; Cineamo {new Date().getFullYear()}</span>

                        <StaticFooterItem href={process.env.NEXT_PUBLIC_ENV_B2B_MARKETING_WEBSITE_HOST_NAME}>
                            {t('common.static-footer.cineamo-for-business')}
                        </StaticFooterItem>
                    </div>

                    <div
                        className={classnames(
                            'relative items-center justify-center sm:justify-start',
                            'order-0 xl:order-1',
                            'grid place-items-center sm:place-items-start',
                            'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
                            'gap-y-8 gap-x-16',
                            'xl:flex xl:flex-wrap xl:justify-start',
                            'xl:space-x-24'
                        )}>
                        <StaticFooterItem href={imprintUrl}>{t('common.static-footer.imprint')}</StaticFooterItem>

                        <StaticFooterItem href={dataProtectionUrl}>
                            {t('common.static-footer.data-security')}
                        </StaticFooterItem>

                        <StaticFooterItem onClick={showPrivacySettingsModal}>
                            {t('common.static-footer.privacy-settings')}
                        </StaticFooterItem>

                        <div className="flex-none hidden lg:block">
                            <LanguageDropdown />
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
